import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Play, ArrowRight, ArrowCounterClockwise, Stop, SpeakerSlash, SpeakerHigh, ClosedCaptioning } from "@phosphor-icons/react";
import { useKidsSound, getKidsSoundOn, setKidsSoundOn } from "@/hooks/useKidsSound";
import { speakLumi, stopLumiAudio, claimPlayback, releasePlayback } from "@/lib/lumiAudio";
import { LUMI_POSES } from "@/data/lumiPoses";

// Lavender hero. Meet Lumi = wave clip (silent) while Candy greets → twirl clip with its own soft music → friendly still.
// Idle shows the approved transparent Lumi on lavender; clips (white backgrounds) appear only in a contained player.
const WAVE = { mp4: "/videos/lumi/wave.mp4", webm: "/videos/lumi/wave.webm", poster: "/videos/lumi/wave-poster.jpg" };
const TWIRL = { mp4: "/videos/lumi/twirl.mp4", webm: "/videos/lumi/twirl.webm", poster: "/videos/lumi/twirl-poster.jpg" };
const MUSIC_VOLUME = 0.18;
export const greetingFor = (nickname) => `Hi, ${nickname?.trim() || "friend"}! I'm happy you're here. Let's learn together!`;

const canPlay = (t) => { try { return !!document.createElement("video").canPlayType(t); } catch { return false; } };
const srcFor = (c) => (canPlay('video/mp4; codecs="avc1.42E01E"') ? c.mp4 : c.webm);

const waitFor = (video, event) => new Promise((resolve) => {
  const done = () => { video.removeEventListener(event, done); video.removeEventListener("error", done); resolve(); };
  video.addEventListener(event, done, { once: true });
  video.addEventListener("error", done, { once: true });
});

export const KidsLandingHero = ({ player, onChooseActivity }) => {
  const [soundOn] = useKidsSound();
  const reduce = useReducedMotion();
  const videoRef = useRef(null);
  const seq = useRef(0); // incremented to cancel an in-flight sequence
  const [phase, setPhase] = useState("idle"); // idle | wave | twirl | done
  const [clip, setClip] = useState(WAVE);
  const [cc, setCc] = useState(() => localStorage.getItem("kids_cc") !== "off");
  const toggleCc = () => setCc((v) => { localStorage.setItem("kids_cc", v ? "off" : "on"); return !v; });
  const greeting = greetingFor(player?.name);
  const active = phase === "wave" || phase === "twirl";

  const stopRef = useRef(null);
  if (!stopRef.current) {
    stopRef.current = () => {
      seq.current += 1;
      const v = videoRef.current;
      if (v && !v.paused) v.pause();
      stopLumiAudio();
      setPhase((p) => (p === "idle" ? "idle" : "done"));
    };
  }
  const stopAll = stopRef.current;

  useEffect(() => () => { stopAll(); releasePlayback(stopAll); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (videoRef.current) videoRef.current.muted = !soundOn; }, [soundOn]);

  const loadAndPlay = async (src, { withSound }) => {
    const v = videoRef.current;
    if (!v) return;
    setClip(src);
    v.src = srcFor(src); // set directly so load() uses the new clip immediately (not the previous <source>)
    v.poster = src.poster;
    v.load();
    v.muted = !withSound || !getKidsSoundOn();
    v.volume = MUSIC_VOLUME;
    try { await v.play(); } catch { /* keep going; still frame remains visible */ }
  };

  const meet = async () => {
    stopAll();
    const my = seq.current;
    claimPlayback(stopAll);
    setPhase("wave");
    const v = videoRef.current;

    if (!reduce) await loadAndPlay(WAVE, { withSound: false }); // the wave clip stays silent so only Candy's voice is heard
    // Greeting: speakLumi resolves when speech ends (or is stopped); with sound off we wait for the wave to finish instead.
    const spoke = await speakLumi(greeting, { claim: stopAll });
    if (seq.current !== my) return;
    if (v && !reduce && !v.ended && !v.paused && (!spoke || !getKidsSoundOn())) await waitFor(v, "ended"); // silent: let the wave (and its caption) finish
    if (seq.current !== my) return;

    if (reduce) { setPhase("done"); releasePlayback(stopAll); return; }
    setPhase("twirl");
    await loadAndPlay(TWIRL, { withSound: true }); // twirl keeps its own soft music; no speech in the track
    if (seq.current !== my) return;
    if (v && !v.ended) await waitFor(v, "ended");
    if (seq.current !== my) return;
    setPhase("done");
    releasePlayback(stopAll);
  };

  const CTRL = "inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-white/85 border border-[#d9ccf5] font-nunito font-bold text-sm text-[#4a2e8a] hover:bg-white transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0]";
  const showCaption = active && cc && phase === "wave";
  const lumiStage = (
    <div className="relative w-[190px] sm:w-[220px] lg:w-[250px] aspect-[9/16]" data-testid="kids-lumi-twirl" data-phase={phase}>
      <img
        src={LUMI_POSES.waving}
        alt="Lumi the Word Fairy waving hello."
        className={`absolute inset-0 w-full h-full object-contain drop-shadow-[0_14px_30px_rgba(106,63,199,0.28)] transition-opacity duration-300 ${active && !reduce ? "opacity-0" : "opacity-100"}`}
        data-testid="kids-lumi-still"
        draggable={false}
      />
      <div className={`absolute inset-0 rounded-[2rem] bg-[#fffaf0] border border-[#f1e6d6] shadow-[0_14px_36px_rgba(106,63,199,0.18)] overflow-hidden transition-opacity duration-300 ${active && !reduce ? "opacity-100" : "opacity-0 pointer-events-none"}`} aria-hidden={!active}>
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain"
          poster={clip.poster}
          preload="auto"
          playsInline
          muted
          aria-label={phase === "twirl" ? "Lumi twirls." : "Lumi waves hello."}
          data-testid="kids-lumi-twirl-video"
        />
      </div>
      {showCaption && (
        <p className="absolute left-2 right-2 bottom-3 rounded-xl bg-black/70 text-white font-nunito font-bold text-xs sm:text-sm text-center px-3 py-2 leading-snug" role="status" aria-live="polite" data-testid="meet-lumi-caption">
          {greeting}
        </p>
      )}
    </div>
  );

  const label = phase === "idle" ? "Meet Lumi" : active ? "Stop" : "Replay welcome";
  const Icon = phase === "idle" ? Play : active ? Stop : ArrowCounterClockwise;

  return (
    <section
      className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#efe6ff] via-[#f1e8fb] to-[#e3dcfa] border border-[#e2d8f7] shadow-[0_12px_40px_rgba(115,88,179,0.12)]"
      data-testid="kids-welcome"
      aria-labelledby="kids-hero-title"
    >
      <span aria-hidden="true" className="absolute left-6 top-5 text-[#f2b544] text-xl">✦</span>
      <span aria-hidden="true" className="absolute left-10 bottom-8 text-[#f48fb1] text-base">✦</span>
      <span aria-hidden="true" className="absolute right-[36%] top-8 text-[#f2b544] text-sm hidden lg:inline">✦</span>
      <span aria-hidden="true" className="absolute right-8 top-1/2 text-[#f48fb1] text-sm hidden lg:inline">✦</span>
      {/* One tree for both modes (the video must not remount): text hides during playback; stage + controls stay. */}
      <div className={`grid gap-6 lg:gap-8 items-center px-6 sm:px-10 lg:px-12 pt-6 pb-6 sm:py-6 ${active ? "grid-cols-1 justify-items-center" : "lg:grid-cols-5"}`} data-mode={active ? "playback" : "text"} data-testid="kids-welcome-layout">
        <div className={`lg:col-span-3 relative ${active ? "hidden" : ""}`} data-testid="kids-welcome-text">
          <p className="font-display italic font-semibold text-xl sm:text-2xl text-[#4a2e8a]" data-testid="kids-hero-eyebrow">Hi, I’m Lumi!</p>
          <h2 id="kids-hero-title" className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#3d2378] leading-[1.02] tracking-tight mt-1.5">
            Let’s make words<br className="hidden sm:block" /> an adventure.
          </h2>
          <p className="font-nunito text-base md:text-lg text-[#5b5572] mt-3 max-w-xl leading-relaxed" data-testid="kids-intro-copy">
            Play, build sentences, and discover something new—one step at a time.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button
              type="button"
              onClick={onChooseActivity}
              className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[#15a6a0] hover:bg-[#108f8a] text-white font-nunito font-extrabold text-base shadow-[0_10px_26px_rgba(21,166,160,0.35)] transition-colors focus-visible:ring-4 focus-visible:ring-[#9fe3df]"
              data-testid="kids-choose-activity-btn"
            >
              Choose an activity <ArrowRight size={20} weight="bold" />
            </button>
            {!active && (
              <button
                type="button"
                onClick={meet}
                className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-white/80 border-2 border-[#6a3fc7] text-[#4a2e8a] font-nunito font-extrabold text-base hover:bg-white transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
                data-testid="meet-lumi-btn"
                data-phase={phase}
              >
                <span className="w-7 h-7 rounded-full bg-[#6a3fc7] text-white flex items-center justify-center"><Icon size={14} weight={Icon === Play ? "fill" : "bold"} className={Icon === Play ? "ml-0.5" : ""} /></span>
                {label}
              </button>
            )}
          </div>
          {!soundOn && (
            <p className="mt-3 inline-flex items-center gap-1.5 font-nunito text-sm text-[#6a3fc7]" data-testid="kids-hero-sound-off-note">
              <SpeakerSlash size={16} weight="bold" /> Sound is off — captions (CC) show Lumi’s words.
            </p>
          )}
        </div>

        <div className={active ? "flex flex-col items-center gap-4" : "lg:col-span-2 flex justify-center lg:justify-end"}>
          {lumiStage}
          {active && (
            <div className="flex flex-wrap items-center justify-center gap-2" data-testid="kids-welcome-playback-controls">
              <button type="button" onClick={stopAll} className={CTRL} data-testid="meet-lumi-btn" data-phase={phase}>
                <Stop size={18} weight="fill" /> Stop
              </button>
              <button type="button" onClick={() => setKidsSoundOn(!soundOn)} aria-pressed={soundOn} aria-label={soundOn ? "Turn sound off" : "Turn sound on"} className={CTRL} data-testid="meet-lumi-sound-btn">
                {soundOn ? <SpeakerHigh size={18} weight="fill" /> : <SpeakerSlash size={18} weight="fill" />} {soundOn ? "Sound on" : "Sound off"}
              </button>
              <button type="button" onClick={toggleCc} aria-pressed={cc} aria-label={cc ? "Hide captions" : "Show captions"} className={`${CTRL} ${cc ? "ring-2 ring-[#6a3fc7]" : ""}`} data-testid="meet-lumi-cc-btn">
                <ClosedCaptioning size={18} weight={cc ? "fill" : "regular"} /> CC
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
